/**
 * Client preferences: localStorage + export/import.
 * Never store passwords.
 */

export const PREFS_KEY = "mdhoria.prefs.v1";
export const PREFS_VERSION = 3;
export const HISTORY_MAX = 200;
export const CLIENT_BUILD = "1d-ambient";

/** Shown in empty Aliases editor (comments only — not active until user uncomments / edits). */
export const EXAMPLE_ALIASES_HELP = `# Examples — each line is: shortname fullcommand
# Delete the # at the start of a line to turn it on, or write your own.
#
# s south
# n north
# e east
# w west
# u up
# d down
# l look
# i inventory
# sc score
# gs get all corpse
# ga get all
# c cast 'cure light'
`;

/** Shown in empty Triggers editor. */
export const EXAMPLE_TRIGGERS_HELP = `# Examples — each line is: text to watch for  =>  what to do
# Delete the # at the start of a line to turn it on.
#
# When someone logs in, play a sound:
# New logon => !beep
#
# When you see this exact text, automatically look at Bob:
# Bob has arrived. => look bob
#
# Sound AND a command:
# You are hungry. => !beep
#
# Someone arrives (any name ending with "has arrived.") — advanced, optional:
# /has arrived\\.?$/i => !beep
#
# Put a minus sign in front to keep a line but leave it off:
# -Old pattern I do not want => look
`;

export const DEFAULT_DIR_ALIASES: Record<string, string> = {
  n: "north",
  s: "south",
  e: "east",
  w: "west",
  u: "up",
  d: "down",
};

export type FontSize = "small" | "medium" | "large";

/** Output trigger (GMud-style): match mud text → command and/or sound. */
export interface Trigger {
  enabled: boolean;
  /** Literal substring, or /regex/flags */
  pattern: string;
  /** Command to send (may be empty if sound-only). Supports $1..$9 for regex captures. */
  command: string;
  sound: boolean;
}

export interface CharPrefs {
  aliases: Record<string, string>;
  history: string[];
  triggers: Trigger[];
}

export interface ClientPrefs {
  version: number;
  wsUrl: string;
  autoReconnect: boolean;
  fontSize: FontSize;
  highContrast: boolean;
  exitHotkeys: boolean;
  showTimestamps: boolean;
  /** Append mud output to an in-browser log (download from Settings). */
  loggingEnabled: boolean;
  activeProfile: string;
  globalAliases: Record<string, string>;
  globalTriggers: Trigger[];
  characters: Record<string, CharPrefs>;
}

const DEFAULT_PROFILE = "default";

export function defaultWsUrl(): string {
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.host}/ws`;
}

export function emptyCharPrefs(): CharPrefs {
  return { aliases: {}, history: [], triggers: [] };
}

export function defaultPrefs(): ClientPrefs {
  return {
    version: PREFS_VERSION,
    wsUrl: defaultWsUrl(),
    autoReconnect: true,
    fontSize: "medium",
    highContrast: false,
    exitHotkeys: false,
    showTimestamps: false,
    loggingEnabled: false,
    activeProfile: DEFAULT_PROFILE,
    globalAliases: { ...DEFAULT_DIR_ALIASES },
    globalTriggers: [],
    characters: {
      [DEFAULT_PROFILE]: emptyCharPrefs(),
    },
  };
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function sanitizeAliases(raw: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  if (!isRecord(raw)) return out;
  for (const [k, v] of Object.entries(raw)) {
    const key = k.trim().toLowerCase();
    if (!key || key.includes(" ") || typeof v !== "string") continue;
    const exp = v.trim();
    if (!exp) continue;
    if (key.length > 32 || exp.length > 200) continue;
    out[key] = exp;
  }
  return out;
}

function sanitizeHistory(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((x): x is string => typeof x === "string" && x.length > 0 && x.length < 500)
    .slice(-HISTORY_MAX);
}

function sanitizeTrigger(raw: unknown): Trigger | null {
  if (!isRecord(raw)) return null;
  const pattern = typeof raw.pattern === "string" ? raw.pattern.trim() : "";
  if (!pattern || pattern.length > 200) return null;
  const command = typeof raw.command === "string" ? raw.command.trim().slice(0, 200) : "";
  const sound = raw.sound === true;
  if (!command && !sound) return null;
  return {
    enabled: raw.enabled !== false,
    pattern,
    command,
    sound,
  };
}

function sanitizeTriggers(raw: unknown): Trigger[] {
  if (!Array.isArray(raw)) return [];
  const out: Trigger[] = [];
  for (const item of raw) {
    const t = sanitizeTrigger(item);
    if (t) out.push(t);
    if (out.length >= 100) break;
  }
  return out;
}

function sanitizeChar(raw: unknown): CharPrefs {
  if (!isRecord(raw)) return emptyCharPrefs();
  return {
    aliases: sanitizeAliases(raw.aliases),
    history: sanitizeHistory(raw.history),
    triggers: sanitizeTriggers(raw.triggers),
  };
}

export function normalizePrefs(raw: unknown): ClientPrefs {
  const base = defaultPrefs();
  if (!isRecord(raw)) return base;

  const priorVersion =
    typeof raw.version === "number" && Number.isFinite(raw.version)
      ? raw.version
      : 1;

  const p: ClientPrefs = {
    version: PREFS_VERSION,
    wsUrl:
      typeof raw.wsUrl === "string" && raw.wsUrl.trim()
        ? raw.wsUrl.trim()
        : base.wsUrl,
    autoReconnect: raw.autoReconnect !== false,
    fontSize:
      raw.fontSize === "small" || raw.fontSize === "large" || raw.fontSize === "medium"
        ? raw.fontSize
        : "medium",
    highContrast: raw.highContrast === true,
    exitHotkeys: raw.exitHotkeys === true,
    showTimestamps: raw.showTimestamps === true,
    loggingEnabled: raw.loggingEnabled === true,
    activeProfile:
      typeof raw.activeProfile === "string" && raw.activeProfile.trim()
        ? raw.activeProfile.trim().slice(0, 32)
        : DEFAULT_PROFILE,
    globalAliases: sanitizeAliases(raw.globalAliases),
    globalTriggers: sanitizeTriggers(raw.globalTriggers),
    characters: {},
  };

  if (priorVersion < 2) {
    p.exitHotkeys = false;
    for (const [k, v] of Object.entries(DEFAULT_DIR_ALIASES)) {
      if (p.globalAliases[k] === undefined) p.globalAliases[k] = v;
    }
  }

  if (isRecord(raw.characters)) {
    for (const [name, cp] of Object.entries(raw.characters)) {
      const n = name.trim().slice(0, 32);
      if (!n) continue;
      p.characters[n] = sanitizeChar(cp);
    }
  }

  if (Object.keys(p.characters).length === 0 && isRecord(raw.aliases)) {
    p.characters[DEFAULT_PROFILE] = {
      aliases: sanitizeAliases(raw.aliases),
      history: sanitizeHistory(raw.history),
      triggers: sanitizeTriggers(raw.triggers),
    };
  }

  if (!p.characters[p.activeProfile]) {
    p.characters[p.activeProfile] = emptyCharPrefs();
  }

  try {
    const oldUrl = localStorage.getItem("mdhoria.wsUrl");
    if (oldUrl && (!raw.wsUrl || raw.wsUrl === base.wsUrl)) {
      p.wsUrl = oldUrl;
    }
  } catch {
    /* ignore */
  }

  return p;
}

export function loadPrefs(): ClientPrefs {
  try {
    const raw = localStorage.getItem(PREFS_KEY);
    if (!raw) {
      const p = defaultPrefs();
      try {
        const old = localStorage.getItem("mdhoria.wsUrl");
        if (old) p.wsUrl = old;
      } catch {
        /* ignore */
      }
      return p;
    }
    return normalizePrefs(JSON.parse(raw) as unknown);
  } catch {
    return defaultPrefs();
  }
}

export function savePrefs(prefs: ClientPrefs): void {
  const clean = normalizePrefs(prefs);
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(clean));
  } catch (e) {
    console.warn("Could not save prefs", e);
  }
}

export function exportPrefsJson(prefs: ClientPrefs): string {
  return JSON.stringify(normalizePrefs(prefs), null, 2);
}

export function importPrefsJson(text: string): ClientPrefs {
  return normalizePrefs(JSON.parse(text) as unknown);
}

export function ensureProfile(prefs: ClientPrefs, name: string): CharPrefs {
  const n = name.trim().slice(0, 32) || DEFAULT_PROFILE;
  if (!prefs.characters[n]) {
    prefs.characters[n] = emptyCharPrefs();
  }
  const ch = prefs.characters[n]!;
  if (!ch.triggers) ch.triggers = [];
  return ch;
}

export function getActiveChar(prefs: ClientPrefs): CharPrefs {
  return ensureProfile(prefs, prefs.activeProfile);
}

export function expandAlias(
  line: string,
  prefs: ClientPrefs
): { line: string; expanded: boolean; from?: string } {
  const trimmed = line.trim();
  if (!trimmed) return { line, expanded: false };

  const sp = trimmed.indexOf(" ");
  const head = (sp === -1 ? trimmed : trimmed.slice(0, sp)).toLowerCase();
  const rest = sp === -1 ? "" : trimmed.slice(sp + 1);

  const charPrefs = getActiveChar(prefs);
  const exp = charPrefs.aliases[head] ?? prefs.globalAliases[head];
  if (!exp) return { line, expanded: false };

  return { line: rest ? `${exp} ${rest}` : exp, expanded: true, from: head };
}

export function pushHistory(prefs: ClientPrefs, command: string): void {
  const ch = getActiveChar(prefs);
  if (!command || command.length > 500) return;
  if (ch.history[ch.history.length - 1] === command) return;
  ch.history.push(command);
  if (ch.history.length > HISTORY_MAX) {
    ch.history = ch.history.slice(-HISTORY_MAX);
  }
}

export function listProfiles(prefs: ClientPrefs): string[] {
  return Object.keys(prefs.characters).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
}

export function aliasesToText(aliases: Record<string, string>): string {
  const body = Object.keys(aliases)
    .sort()
    .map((k) => `${k} ${aliases[k]}`)
    .join("\n");
  return body;
}

/** Editor text: real aliases, or friendly examples if none yet. */
export function aliasesEditorText(aliases: Record<string, string>): string {
  const body = aliasesToText(aliases);
  if (body.trim()) return body;
  return EXAMPLE_ALIASES_HELP;
}

export function textToAliases(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t || t.startsWith("#")) continue;
    const sp = t.indexOf(" ");
    if (sp === -1) continue;
    const key = t.slice(0, sp).trim().toLowerCase();
    const exp = t.slice(sp + 1).trim();
    if (!key || !exp || key.includes(" ")) continue;
    if (key.length > 32 || exp.length > 200) continue;
    out[key] = exp;
  }
  return out;
}

/**
 * Trigger text format (one per line):
 *   pattern => command
 *   pattern => !beep
 *   pattern => look bob !beep
 *   /regex/i => command !beep
 *   -pattern => command     (disabled)
 * Lines starting with # are comments.
 */
export function triggersToText(triggers: Trigger[]): string {
  return triggers
    .map((t) => {
      const dis = t.enabled ? "" : "-";
      const right = [t.command, t.sound ? "!beep" : ""].filter(Boolean).join(" ");
      return `${dis}${t.pattern} => ${right || "!beep"}`;
    })
    .join("\n");
}

/** Editor text: real triggers, or friendly examples if none yet. */
export function triggersEditorText(triggers: Trigger[]): string {
  const body = triggersToText(triggers);
  if (body.trim()) return body;
  return EXAMPLE_TRIGGERS_HELP;
}

export function textToTriggers(text: string): Trigger[] {
  const out: Trigger[] = [];
  for (const line of text.split(/\r?\n/)) {
    const t = line.trim();
    if (!t || t.startsWith("#")) continue;
    const arrow = t.indexOf("=>");
    if (arrow === -1) continue;

    let left = t.slice(0, arrow).trim();
    let right = t.slice(arrow + 2).trim();
    let enabled = true;
    if (left.startsWith("-")) {
      enabled = false;
      left = left.slice(1).trim();
    }
    if (!left) continue;

    let sound = false;
    // trailing !beep / !sound
    const beepMatch = right.match(/^(.*?)(?:\s+!(?:beep|sound))\s*$/i);
    if (beepMatch) {
      sound = true;
      right = (beepMatch[1] ?? "").trim();
    } else if (/^!(?:beep|sound)$/i.test(right)) {
      sound = true;
      right = "";
    }

    if (!right && !sound) continue;
    if (left.length > 200 || right.length > 200) continue;

    out.push({
      enabled,
      pattern: left,
      command: right,
      sound,
    });
    if (out.length >= 100) break;
  }
  return out;
}

/** All triggers for the active profile (profile first, then global). */
export function activeTriggers(prefs: ClientPrefs): Trigger[] {
  const ch = getActiveChar(prefs);
  return [...(ch.triggers ?? []), ...(prefs.globalTriggers ?? [])];
}
